import React from 'react';
import { observer, inject } from 'mobx-react';
import moment from 'moment-timezone';
import qs from 'qs';
import Pagination from 'components/Pagination';
import Footer from 'components/Footer';
import { formatTimeOnly, formatDateOnly } from 'helpers/time';
import { currencyFormattedAmount } from 'helpers/currency';
import { INVOICE_COLUMNS, INVOICE_MIN_TABLE_WIDTH } from 'components/Tables/Invoices';
import * as DataList from 'components/DataList';

// A client's invoices (client page, Invoices tab), laid out like the
// Invoices list (components/Tables/Invoices), with the customer column
// showing the invoice's location (every row is this client).
const LOCATION_COLUMN = { area: 'where', label: 'Location', width: 'minmax(180px, 1.3fr)', icon: 'map-marker' };

const COLUMNS = INVOICE_COLUMNS.map((column) => (column.area === 'client' ? LOCATION_COLUMN : column));

const CARDS = {
  columns: 'minmax(0, 1fr) auto auto',
  areas: [
    'invoice status actions',
    'job job job',
    'where where where',
    'due due due',
    'created_by total total',
  ],
  footer: ['created_by', 'total'],
};

const MIN_TABLE_WIDTH = INVOICE_MIN_TABLE_WIDTH;

const isPaid = (attributes) => attributes.payment_status === 'paid';

const isOverdue = (attributes) =>
  !isPaid(attributes) &&
  attributes.invoice_due_date &&
  moment(attributes.invoice_due_date).isBefore(moment(), 'day');

// Payment status pill: the real payment_status, red when an unpaid invoice
// is past its due date (same as the Invoices list).
const PaymentPill = ({ attributes }) => {
  let tone = 'grey';
  if (attributes.payment_status === 'paid') tone = 'green';
  else if (attributes.payment_status === 'partially_paid') tone = 'amber';
  if (isOverdue(attributes)) tone = 'red';
  return <DataList.Pill tone={tone}>{attributes.formatted_payment_status || 'Unpaid'}</DataList.Pill>;
};

@inject('viewStore', 'invoiceStore') @observer
class InvoicesTable extends React.Component {

  componentDidMount() {
    this.props.viewStore.clientInvoicSearch.where.client_id = this.props.client.attributes.id;
    this.props.invoiceStore.loadClientInvoices();
  }

  onPaginationChange(newPage) {
    const { viewStore } = this.props;
    viewStore.clientInvoicSearch = {
      ...viewStore.clientInvoicSearch,
      page: newPage,
    };
    this.props.invoiceStore.loadClientInvoices();
  }

  render() {
    const {
      viewStore,
      viewStore: {
        jobTab: { section },
      },
      invoiceStore: {
        clientInvoices: { objects, isLoading, isError, meta },
      },
    } = this.props;

    if (isLoading && objects.length === 0) {
      return (
        <DataList.Loading columns={COLUMNS} cards={CARDS} minTableWidth={MIN_TABLE_WIDTH} noun="invoices" rows={5} />
      );
    }
    if (isError) {
      return (
        <DataList.LoadError
          noun="invoices"
          onRetry={() => this.onPaginationChange((meta && meta.page) || 1)}
        />
      );
    }
    if (objects.length === 0) {
      return (
        <DataList.Empty
          noun="invoices"
          message="Invoices you create for this client will show up here."
        />
      );
    }

    const pagination = meta && meta.total_pages > 1 && (
      <Footer>
        <div className="bp3-navbar-group bp3-align-left">
          <Pagination value={meta.page} total={meta.total_pages} onChange={this.onPaginationChange.bind(this)} />
        </div>
      </Footer>
    );

    return (
      <React.Fragment>
        <DataList.List
          preferTable
          columns={COLUMNS}
          cards={CARDS}
          minTableWidth={MIN_TABLE_WIDTH}
          total={(meta && meta.total_records) || objects.length}
          noun="invoices"
          note={DataList.sortNote(viewStore.clientInvoicSearch.order)}
        >
          {objects.map((invoice) => {
            const attributes = invoice.attributes;
            const job = attributes.job || {};
            const createdBy = attributes.created_by || {};
            const showLink = `/dashboard/invoices/${invoice.id}/show`;
            const jobLink = job.id ? `/dashboard/jobs/${job.id}/${section}` : null;
            const jobNumber = attributes.event_number || job.event_number;
            const due = !isPaid(attributes) && attributes.due_amount > 0;
            const sent = !isPaid(attributes) && attributes.status === 'sent';
            return (
              <DataList.Row key={invoice.id}>
                <DataList.Cell area="invoice">
                  <div className="dl-lines">
                    <DataList.RowLink href={showLink} label={`Invoice ${attributes.number}`}>
                      #{attributes.number}
                    </DataList.RowLink>
                    <div className="dl-muted dl-truncate">
                      {formatDateOnly(attributes.created_at)}, {formatTimeOnly(attributes.created_at)}
                    </div>
                  </div>
                </DataList.Cell>
                <DataList.Cell area="due">
                  <DataList.Lines
                    primary={attributes.invoice_due_date
                      ? <React.Fragment><DataList.CardOnly>Due </DataList.CardOnly>{formatDateOnly(attributes.invoice_due_date)}</React.Fragment>
                      : <DataList.Muted>No due date</DataList.Muted>}
                    title={attributes.invoice_due_date ? `Due ${formatDateOnly(attributes.invoice_due_date)}` : undefined}
                    strong={false}
                    secondary={attributes.invoice_due_date
                      ? (isOverdue(attributes) ? 'Overdue' : formatTimeOnly(attributes.invoice_due_date))
                      : null}
                  />
                </DataList.Cell>
                <DataList.Cell area="job">
                  {jobNumber ? (
                    <DataList.Lines
                      primary={<React.Fragment><DataList.CardOnly>Job </DataList.CardOnly>#{jobNumber}</React.Fragment>}
                      title={`Job #${jobNumber}`}
                      href={jobLink || undefined}
                      secondary={job.service_type_name}
                    />
                  ) : (
                    <DataList.Muted>No job</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="where">
                  {attributes.location && attributes.location.full_address ? (
                    <DataList.Lines primary={attributes.location.full_address} title={attributes.location.full_address} strong={false} />
                  ) : (
                    <DataList.Muted>No location</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="created_by">
                  {createdBy.full_name ? (
                    <DataList.Lines
                      primary={<React.Fragment><DataList.CardOnly>Created by </DataList.CardOnly>{createdBy.full_name}</React.Fragment>}
                      title={createdBy.full_name}
                      href={createdBy.id
                        ? `/dashboard/users/${createdBy.id}/show?${qs.stringify(viewStore.userShowSearch, { encode: false })}`
                        : undefined}
                      secondary={createdBy.email}
                    />
                  ) : (
                    <DataList.Muted>Unknown</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="total">
                  <DataList.Lines
                    primary={currencyFormattedAmount(attributes.grand_total)}
                    secondary={due ? `${currencyFormattedAmount(attributes.due_amount)} due` : null}
                  />
                </DataList.Cell>
                <DataList.Cell area="status">
                  <div className="dl-lines">
                    <PaymentPill attributes={attributes} />
                    {sent && <div className="dl-muted">{attributes.formatted_status}</div>}
                  </div>
                </DataList.Cell>
                <DataList.Cell area="actions">
                  <DataList.Actions
                    items={[
                      jobLink && { icon: 'eye-open', label: 'View job', href: jobLink },
                      { icon: 'edit', label: 'View or edit invoice', href: showLink },
                    ]}
                  />
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        {pagination}
      </React.Fragment>
    );
  }
}

export default InvoicesTable;
